import { IMsalContext } from "@azure/msal-react";
import {
  S3Client,
  ListObjectVersionsCommand,
  ListObjectVersionsCommandOutput,
  GetObjectCommand,
  ObjectVersion,
  DeleteMarkerEntry,
} from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { fetchAwsCredentials } from "components/Upload/uploadApi";
import { IGetAgCredsResponse } from "components/ObjectList/objectListApi";

export type CombinedVersion = (ObjectVersion | DeleteMarkerEntry) & {
  __type: "Version" | "DeleteMarker";
};

export interface VersionsPage {
  versions: CombinedVersion[];
  isTruncated: boolean;
  nextKeyMarker?: string;
  nextVersionIdMarker?: string;
}

async function makeClient(
  bucket: string,
  region: string,
  msalInstance: IMsalContext,
  agCredsResponseData: IGetAgCredsResponse | boolean,
): Promise<S3Client> {
  let credentials;
  let finalRegion = region;

  // Priority 1: Use agCredsResponseData if available
  if (typeof agCredsResponseData === "object" && "aws_access_key_id" in agCredsResponseData) {
    credentials = {
      accessKeyId: agCredsResponseData.aws_access_key_id,
      secretAccessKey: agCredsResponseData.aws_secret_access_key,
      sessionToken: agCredsResponseData.aws_session_token,
    };
  } else {
    // Priority 2: Fallback to localStorage session
    const baseCreds = await fetchAwsCredentials(msalInstance, bucket);
    if (!baseCreds) throw new Error("No AWS credentials available");
    
    credentials = {
      accessKeyId: baseCreds.accessKeyId,
      secretAccessKey: baseCreds.secretAccessKey,
      sessionToken: baseCreds.sessionToken,
    };
    finalRegion = finalRegion || baseCreds.region;
  }

  return new S3Client({
    region: finalRegion,
    credentials,
  });
}

/** Get one page of versions + delete markers (lazy loading) for a key */
export async function getObjectVersionsPage(
  bucket: string,
  key: string,
  msalInstance: IMsalContext,
  region: string,
  agCredsResponseData: IGetAgCredsResponse | boolean,
  opts?: { keyMarker?: string; versionIdMarker?: string; maxKeys?: number },
): Promise<VersionsPage> {
  const client = await makeClient(bucket, region, msalInstance, agCredsResponseData);

  const cmd = new ListObjectVersionsCommand({
    Bucket: bucket,
    Prefix: key,
    MaxKeys: opts?.maxKeys ?? 10,
    KeyMarker: opts?.keyMarker,
    VersionIdMarker: opts?.versionIdMarker,
  });

  const resp: ListObjectVersionsCommandOutput = await client.send(cmd);

  // Filter to exact key to avoid prefix collisions (handle deleted objects too)
  const keyToMatch = key.replace(" (deleted)", ""); // Remove deleted suffix for matching
  
  const normal: CombinedVersion[] = (resp.Versions ?? [])
    .filter((v) => v.Key === keyToMatch)
    .map(v => ({ ...v, __type: "Version" as const }));

  const markers: CombinedVersion[] = (resp.DeleteMarkers ?? [])
    .filter((d) => d.Key === keyToMatch)
    .map(d => ({ ...d, __type: "DeleteMarker" as const }));

  // Merge and sort by LastModified (desc)
  const merged = [...normal, ...markers].sort((a, b) => {
    const at = a.LastModified ? new Date(a.LastModified as any).getTime() : 0;
    const bt = b.LastModified ? new Date(b.LastModified as any).getTime() : 0;
    return bt - at;
  });

  return {
    versions: merged,
    isTruncated: !!resp.IsTruncated,
    nextKeyMarker: resp.NextKeyMarker,
    nextVersionIdMarker: resp.NextVersionIdMarker,
  };
}

/** Presigned download URL for a specific VersionId (5 minutes) */
export async function getObjectVersionDownloadUrl(
  bucket: string,
  key: string,
  versionId: string,
  msalInstance: IMsalContext,
  region: string,
  agCredsResponseData: IGetAgCredsResponse | boolean,
): Promise<string> {
  const client = await makeClient(bucket, region, msalInstance, agCredsResponseData);
  const cmd = new GetObjectCommand({
    Bucket: bucket,
    Key: key,
    VersionId: versionId,
    ResponseContentDisposition: `attachment; filename="${key.split("/").pop()}"`,
  });
  return getSignedUrl(client, cmd, { expiresIn: 300 });
}
